import { ListenableSubscribable } from '@benzinga/subscribable';
import { ChartExtendedContent, ChartExtendedMetaInfo } from './entities';
import { arrayDifference } from '@benzinga/utils';

interface ChartConfigUpdateEvent {
  type: 'chart-config:visible-charts-updated' | 'chart-config:hidden-charts-updated';
  charts: ChartExtendedMetaInfo[];
}

interface ChartConfigExtendedUpdateEvent {
  charts: ChartExtendedContent[];
  type: 'chart-config:saved-charts-updated' | 'chart-config:drawing-charts-updated';
}

interface ChartUpdatedEvent {
  chart: ChartExtendedContent;
  type: 'chart-config:chart-updated' | 'chart-config:post-chart-updated';
  uuid: string | null;
}

export type ChartConfigStoreEvent = ChartConfigUpdateEvent | ChartConfigExtendedUpdateEvent | ChartUpdatedEvent;

export class ChartConfigStore extends ListenableSubscribable<ChartConfigStoreEvent> {
  private date?: Date;
  private charts: ChartExtendedMetaInfo[] | undefined;
  private visibleCharts: ChartExtendedMetaInfo[] | undefined;
  private hiddenCharts: ChartExtendedMetaInfo[] | undefined;

  private savedCharts: ChartExtendedContent[] | undefined;
  private drawingCharts: ChartExtendedContent[] | undefined;

  private chartsData = new Map<string, ChartExtendedContent>();

  constructor() {
    super();
    this.visibleCharts = undefined;
    this.hiddenCharts = undefined;
  }

  public setCharts = (charts: ChartExtendedMetaInfo[]): void => {
    this.charts = charts;
    const visibleCharts = charts
      .filter(chart => !chart.hidden)
      .sort((lch, rch) => (lch.name.toLocaleLowerCase() > rch.name.toLocaleLowerCase() ? 1 : -1)); // names are unique

    const hiddenCharts = charts
      .filter(chart => chart.hidden)
      .sort((lch, rch) => (lch.name.toLocaleLowerCase() > rch.name.toLocaleLowerCase() ? 1 : -1)); // names are unique)

    if (arrayDifference(this.visibleCharts ?? [], visibleCharts)) {
      this.visibleCharts = visibleCharts;
      this.dispatch({
        charts: visibleCharts,
        type: 'chart-config:visible-charts-updated',
      });
    }

    if (arrayDifference(this.hiddenCharts ?? [], hiddenCharts)) {
      this.hiddenCharts = hiddenCharts;
      this.dispatch({
        charts: hiddenCharts,
        type: 'chart-config:hidden-charts-updated',
      });
    }
  };

  public setSavedCharts = (charts: ChartExtendedContent[]): void => {
    const visibleCharts = charts.sort((lch, rch) =>
      lch.name.toLocaleLowerCase() > rch.name.toLocaleLowerCase() ? 1 : -1,
    ); // names are unique

    if (arrayDifference(this.savedCharts ?? [], visibleCharts)) {
      this.savedCharts = visibleCharts;
      this.dispatch({
        charts: visibleCharts,
        type: 'chart-config:saved-charts-updated',
      });
    }
  };

  public setDrawingCharts = (charts: ChartExtendedContent[]): void => {
    const visibleCharts = charts.sort((lch, rch) =>
      lch.name.toLocaleLowerCase() > rch.name.toLocaleLowerCase() ? 1 : -1,
    ); // names are unique

    if (arrayDifference(this.drawingCharts ?? [], visibleCharts)) {
      this.drawingCharts = visibleCharts;
      this.dispatch({
        charts: visibleCharts,
        type: 'chart-config:drawing-charts-updated',
      });
    }
  };

  public shouldWeFetchCharts = (): boolean => {
    const ONE_MIN = 60 * 1000; /* ms */
    const lastCalled = this.date?.getTime() ?? 0;
    if (this.charts === undefined || Date.now() - lastCalled > ONE_MIN) {
      return true;
    }
    return false;
  };

  public getCharts = (): ChartExtendedMetaInfo[] | undefined => {
    return this.charts;
  };

  public getCachedVisibleCharts = (): ChartExtendedMetaInfo[] | undefined => {
    return this.visibleCharts;
  };

  public getCachedHiddenCharts = (): ChartExtendedMetaInfo[] | undefined => {
    return this.hiddenCharts;
  };

  public getCachedSavedCharts = (): ChartExtendedContent[] | undefined => {
    return this.savedCharts;
  };

  public getCachedDrawingCharts = (): ChartExtendedContent[] | undefined => {
    return this.drawingCharts;
  };

  public getCachedChart = (guid: string) => this.chartsData.get(guid);

  public setCachedChart = (guid: string, chart: ChartExtendedContent) => {
    this.chartsData.set(guid, chart);

    if (this.charts?.find(c => c.guid === guid)) {
      this.setCharts(this.charts.map(c => (c.guid === guid ? { ...c, ...chart } : c)));
    } else {
      this.setCharts([
        ...(this.charts ?? []),
        { ...chart, guid, hidden: chart.hidden ?? false, timestamp: chart.timestamp ?? Date.now() },
      ]);
    }

    this.dispatch({
      chart: chart,
      type: 'chart-config:chart-updated',
      uuid: guid,
    });
  };
}
